import React from 'react';
import { StyledButton, StyledContainer, StyledUnlockButton } from '@apps/guest/packages/layout-engine/components/CtaButtons/CtaButtons.styles';
import { useCtaButtonsController } from '@apps/guest/packages/layout-engine/components/CtaButtons/CtaButtons.controller';
import { TextV2 } from '@withjoy/joykit';
import type { GraphQLError } from 'graphql';
import { useColorPaletteProvider } from '../../layouts/ColorPaletteProvider/ColorPaletteProvider';
import { useEventLocaleFormatter } from '@shared/core/localization/hooks/useEventLocaleFormatter';
import { useTranslation } from '@shared/core';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { body4ToSubHeadingOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';

export interface CtaButtonsProps
  extends Readonly<{
    hideRsvp?: boolean;
    hideUnlock?: boolean;
    hideGuestName?: boolean;
    onRSVPButtonClicked?: () => void;
    onNotYouButtonClicked?: () => void;
    onNotYouErrorRequest?: (err: GraphQLError) => void;
    customRsvpButtonText?: string;
    customUnlockButtonText?: string;
  }> {}

const CtaButtons: React.FC<CtaButtonsProps> = ({ onRSVPButtonClicked, onNotYouButtonClicked, onNotYouErrorRequest, customRsvpButtonText, customUnlockButtonText }) => {
  const {
    isAdmin,
    guestName,
    personalizedText,
    buttonNotYouText,
    rsvpButtonText,
    shouldDisplayRSVPButton,
    rsvpHandle,
    logoutHandle,
    shouldDisplayUnlockButton,
    isSpecificGuestsOnlyUnlock,
    hasUnrevealedSpecificGuestPages,
    unlockButtonText,
    showDeadlineToGuest,
    deadlineDate,
    deadlineTimezone,
    showDeadlineNote,
    deadlineNote,
  } = useCtaButtonsController({
    onRSVPButtonClicked,
    onNotYouButtonClicked,
    onNotYouErrorRequest,
    customRsvpButtonText,
    customUnlockButtonText,
  });

  const { rsvpButtonColor, pageFontColor } = useColorPaletteProvider();
  const { formatDate } = useEventLocaleFormatter();
  const { tq } = useTranslation('layoutEngine');
  const formattedDeadline = shouldDisplayRSVPButton && showDeadlineToGuest && deadlineDate ? formatDate(deadlineDate, 'MMMM do, yyyy', { timezone: deadlineTimezone }) : null;
  // `showDeadlineNote` already requires the RSVP button to have been visible
  // pre-deadline; include it here so the note shows even for anonymous guests.
  const shouldRender = (!isAdmin && guestName) || shouldDisplayUnlockButton || shouldDisplayRSVPButton || showDeadlineNote;

  if (!shouldRender) {
    return null;
  }

  return (
    <StyledContainer accentColor={rsvpButtonColor}>
      {!isAdmin && guestName && (
        <TextV2
          data-testid="identity"
          textAlign="center"
        >
          {personalizedText}
          <StyledButton
            variant="link"
            onClick={logoutHandle}
            display="inline"
            width="auto"
            paddingLeft={2}
            __css={{ textDecoration: 'underline' }}
          >
            {buttonNotYouText}
          </StyledButton>
        </TextV2>
      )}
      {shouldDisplayUnlockButton && (
        <StyledUnlockButton
          unlockButtonText={unlockButtonText}
          shouldDisplayUnlockButton={shouldDisplayUnlockButton}
          isSpecificGuestsUnlock={isSpecificGuestsOnlyUnlock}
          hasUnrevealedSpecificGuestPages={hasUnrevealedSpecificGuestPages}
          position="relative"
          variant="outline"
          intent="primary"
          data-testid="unlock-btn"
        />
      )}
      {shouldDisplayRSVPButton && (
        <>
          <StyledButton
            width="100%"
            shape="rounded"
            variant="outline"
            onClick={rsvpHandle}
            data-testid="rsvp-btn"
          >
            {rsvpButtonText}
          </StyledButton>
          {formattedDeadline && (
            <TextV2
              textAlign="center"
              typographyVariant="label2"
              data-testid="rsvp-deadline"
            >
              {tq`RSVP by ${formattedDeadline}`}
            </TextV2>
          )}
        </>
      )}
      {showDeadlineNote && (
        // Match the welcome greeting's treatment (body4 + sub-heading font) and
        // reset the inherited RSVP accent color so the note reads as body copy.
        <GuestSiteTypographyOverride override={body4ToSubHeadingOverride}>
          <TextV2
            textAlign="center"
            typographyVariant="body4"
            color={pageFontColor}
            data-testid="rsvp-deadline-note"
            whiteSpace="pre-wrap"
            wordBreak="break-word"
          >
            {deadlineNote}
          </TextV2>
        </GuestSiteTypographyOverride>
      )}
    </StyledContainer>
  );
};

CtaButtons.displayName = 'CtaButtons';

export { CtaButtons };
